import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import type {
  DataGridProps,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
} from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';

export type BuildServerDataTableGridPropsArgs<T extends GridValidRowModel> = {
  autoHeight: boolean;
  captureScrollAnchor: () => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  columns: ReadonlyArray<GridColDef<T>>;
  dataGridSx?: SxProps<Theme>;
  disableColumnMenu: boolean;
  disableColumnResize?: boolean;
  disableColumnSorting?: boolean;
  disablePageSizeSelect?: boolean;
  emptyOverlayHeight?: number;
  effectiveErrorMessage: string | null;
  effectiveRowCount: number;
  effectiveRowsData: T[];
  externalLoading?: boolean;
  filterModel: GridFilterModel;
  getRowHeight?: DataGridProps['getRowHeight'];
  getRowId: (row: T) => GridRowId;
  gridProps?: Partial<DataGridProps>;
  handleColumnWidthChange: NonNullable<DataGridProps['onColumnWidthChange']>;
  handleFilterModelChange: (model: GridFilterModel) => void;
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  handleSortModelChange: (model: GridSortModel) => void;
  hideFooter?: boolean;
  hidePaginationControls?: boolean;
  onCellClick?: (params: GridCellParams<T>) => void;
  onColumnHeaderClick?: DataGridProps['onColumnHeaderClick'];
  onRowClick?: (params: GridRowParams<T>) => void;
  paginationModel: GridPaginationModel;
  queryIsFetching?: boolean;
  resolvedNoRowsText: string;
  resolvedPageSizeOptions: number[];
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  showCellVerticalBorder?: boolean;
  showColumnVerticalBorder?: boolean;
  showToolbar: boolean;
  sortModel: GridSortModel;
  suppressFocusOutline?: boolean;
};

export const buildServerDataTableGridProps = <T extends GridValidRowModel>({
  autoHeight,
  captureScrollAnchor,
  columnVisibilityModel,
  columns,
  dataGridSx,
  disableColumnMenu,
  disableColumnResize,
  disableColumnSorting,
  disablePageSizeSelect,
  emptyOverlayHeight,
  effectiveErrorMessage,
  effectiveRowCount,
  effectiveRowsData,
  externalLoading: _externalLoading,
  filterModel,
  getRowHeight,
  getRowId,
  gridProps,
  handleColumnWidthChange,
  handleFilterModelChange,
  handlePaginationModelChange,
  handleSortModelChange,
  hideFooter,
  hidePaginationControls,
  onCellClick,
  onColumnHeaderClick,
  onRowClick,
  paginationModel,
  queryIsFetching: _queryIsFetching,
  resolvedNoRowsText,
  resolvedPageSizeOptions,
  setColumnVisibilityModel,
  showCellVerticalBorder,
  showColumnVerticalBorder,
  showToolbar,
  sortModel,
  suppressFocusOutline,
}: BuildServerDataTableGridPropsArgs<T>): DataGridProps => {
  const resolvedDisableColumnResize = disableColumnResize ?? false;
  const resolvedDisableColumnSorting = disableColumnSorting ?? true;
  const resolvedDisablePageSizeSelect = disablePageSizeSelect ?? false;
  const resolvedHideFooter = hideFooter ?? false;
  const resolvedSuppressFocusOutline = suppressFocusOutline ?? false;
  const zeroRowSpacing: NonNullable<DataGridProps['getRowSpacing']> = () => ({
    top: 0,
    bottom: 0,
  });
  const resolvedDataGridSx: SxProps<Theme> = [
    ...(dataGridSx ? (Array.isArray(dataGridSx) ? dataGridSx : [dataGridSx]) : []),
    resolvedHideFooter
      ? {
          '& .MuiDataGrid-main': {
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          },
        }
      : null,
  ].filter(Boolean) as SxProps<Theme>;
  const {
    onColumnVisibilityModelChange: gridOnColumnVisibilityModelChange,
    slotProps: gridSlotProps = {},
    ...restGridProps
  }: Partial<DataGridProps> = gridProps ?? {};
  const handleColumnHeaderClick: NonNullable<DataGridProps['onColumnHeaderClick']> = (
    params,
    event,
    details,
  ) => {
    captureScrollAnchor();
    onColumnHeaderClick?.(params, event, details);
  };

  return {
    rows: effectiveRowsData,
    columns,
    loading: false,
    disableRowSelectionOnClick: true,
    rowSelection: false,
    disableColumnMenu,
    disableColumnSorting: resolvedDisableColumnSorting,
    disableColumnResize: resolvedDisableColumnResize,
    showColumnVerticalBorder,
    showCellVerticalBorder,
    suppressFocusOutline: resolvedSuppressFocusOutline,
    hideFooterSelectedRowCount: true,
    pageSizeOptions: resolvedDisablePageSizeSelect ? [] : resolvedPageSizeOptions,
    paginationMode: 'server',
    sortingMode: 'server',
    filterMode: 'server',
    paginationModel,
    onPaginationModelChange: handlePaginationModelChange,
    rowCount: effectiveRowCount,
    sortModel,
    onSortModelChange: handleSortModelChange,
    onColumnHeaderClick: handleColumnHeaderClick,
    filterModel,
    onFilterModelChange: handleFilterModelChange,
    columnVisibilityModel,
    onColumnVisibilityModelChange: (...args) => {
      const [model, details] = args as Parameters<
        NonNullable<DataGridProps['onColumnVisibilityModelChange']>
      >;
      setColumnVisibilityModel(model);
      gridOnColumnVisibilityModelChange?.(model, details);
    },
    onColumnWidthChange: handleColumnWidthChange,
    onRowClick,
    onCellClick,
    getRowId,
    autoHeight,
    getRowHeight,
    getRowSpacing: zeroRowSpacing,
    rowSpacingType: 'border',
    showToolbar,
    hideFooter: resolvedHideFooter,
    hidePaginationControls,
    sx: resolvedDataGridSx,
    emptyTitle: effectiveErrorMessage ?? resolvedNoRowsText,
    error: Boolean(effectiveErrorMessage),
    errorTitle: effectiveErrorMessage ?? undefined,
    overlayHeight: emptyOverlayHeight,
    slotProps: {
      ...gridSlotProps,
    },
    ...restGridProps,
  };
};
